// The task drawer's Files section (TASK-FILES; Work OS's task attachments, design
// 2026-10-22-task-files §Drawer): attach by the button, by dropping files on the section or by
// pasting them anywhere in the drawer (outside a text field); folders of any depth with a
// breadcrumb; each file a row with its preview, open, download, cover, rename and delete as
// visible hover icons; delete asks first and moves the file to the trash, where it can be
// restored until it is purged (the company's retention, 30 days by default) or "Empty trash"
// purges it now. Comment files show here too ("in a comment"). The server holds the rules (task
// permissions, the size cap, the sniffed type): a refusal is a toast.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  DrawerSection,
  NativeSelect,
  NativeSelectOption,
  Spinner,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveRestoreIcon,
  ChevronRightIcon,
  FolderIcon,
  FolderPlusIcon,
  LayoutGridIcon,
  ListIcon,
  PaperclipIcon,
  PencilIcon,
  StarIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import {
  type FileItem,
  FileRow,
  FileThumb,
  type FolderItem,
  fileKeys,
  formatBytes,
  IconAction,
  iconOf,
  ON_HOVER,
  PreviewDialog,
  RenameDialog,
} from "./files";
import { usePref } from "./task-page/queries";
import type { TaskDetail } from "./view-engine";
import { workKeys } from "./work-queries";

type FileSort = "name" | "date" | "size" | "type";

/** The files in the chosen order (newest first for `date`; ties by name). */
export function sortFiles<T extends Pick<FileItem, "file_name" | "created_at" | "bytes" | "mime">>(
  files: T[],
  sort: FileSort,
): T[] {
  const byName = (a: T, b: T) => a.file_name.localeCompare(b.file_name);
  const cmp: Record<FileSort, (a: T, b: T) => number> = {
    name: byName,
    date: (a, b) => b.created_at.localeCompare(a.created_at) || byName(a, b),
    size: (a, b) => b.bytes - a.bytes || byName(a, b),
    type: (a, b) => a.mime.localeCompare(b.mime) || byName(a, b),
  };
  return [...files].sort(cmp[sort]);
}

type Confirm =
  | { kind: "file"; file: FileItem }
  | { kind: "folder"; folder: FolderItem }
  | { kind: "empty"; count: number };
type Renaming =
  | { kind: "file"; file: FileItem }
  | { kind: "folder"; folder: FolderItem }
  | { kind: "new" };

const daysLeft = (iso: string | null) =>
  iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)) : 0;

/** Is the paste going into a text field (then it is the field's)? */
const editable = (t: EventTarget | null) =>
  t instanceof HTMLElement &&
  (t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA");

export function FilesSection({
  api,
  company,
  task,
}: {
  api: Api;
  company: CompanyAccess;
  task: Pick<TaskDetail, "id">;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const live = useQuery({
    queryKey: fileKeys.live(co, task.id),
    queryFn: () =>
      api.client.files.list({ params: { co }, query: { task_id: task.id, limit: 200 } }),
  });
  const folders = useQuery({
    queryKey: fileKeys.folders(co, task.id),
    queryFn: () => api.client.files.folders({ params: { co, id: task.id } }),
  });
  const [showTrash, setShowTrash] = useState(false);
  const trash = useQuery({
    queryKey: fileKeys.trash(co, task.id),
    queryFn: () =>
      api.client.files.list({
        params: { co },
        query: { task_id: task.id, trashed: "true", limit: 200 },
      }),
  });
  const [here, setHere] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ id: string; name: string }[]>([]);
  const [preview, setPreview] = useState<FileItem | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [renaming, setRenaming] = useState<Renaming | null>(null);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const section = useRef<HTMLDivElement>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: fileKeys.task(co, task.id) });
    void qc.invalidateQueries({ queryKey: workKeys.history(co, task.id) });
    // T4-VIEWS-MEDIA: a new or dropped cover shows on the board's cards, chips and gallery now.
    void qc.invalidateQueries({ queryKey: ["company", co, "work", "files"] });
  };
  const done = { onSuccess: refresh, onSettled: refresh };

  const allFolders = folders.data?.data ?? [];
  // A folder deleted from elsewhere: back to the root.
  useEffect(() => {
    if (here && folders.data && !allFolders.some((f) => f.id === here)) setHere(null);
  }, [here, folders.data, allFolders]);

  const send = useMutation({
    mutationFn: (form: FormData) => api.client.files.upload({ params: { co }, body: form }),
  });

  async function upload(files: File[]) {
    const batch = files.map((f) => ({
      id: crypto.randomUUID(),
      name: f.name || "pasted file",
      file: f,
    }));
    setUploading((u) => [...u, ...batch]);
    await Promise.all(
      batch.map(async (b) => {
        const form = new FormData();
        form.set("file", b.file, b.name);
        form.set("task_id", task.id);
        if (here) form.set("folder_id", here);
        try {
          // A mutation, so a refusal (too large, no access) is the app's problem toast.
          await send.mutateAsync(form);
        } catch {
          // Toasted by the mutation cache.
        } finally {
          setUploading((u) => u.filter((x) => x.id !== b.id));
        }
      }),
    );
    refresh();
  }

  // Paste anywhere in the drawer (not into a text field) attaches the clipboard's files.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])];
      if (!files.length || editable(e.target)) return;
      const drawer = section.current?.closest("[data-slot=drawer-content], [role=dialog]");
      if (
        drawer &&
        e.target instanceof Node &&
        !drawer.contains(e.target) &&
        e.target !== document.body
      )
        return;
      e.preventDefault();
      void upload(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  const update = useMutation({
    mutationFn: (v: {
      id: string;
      body: { file_name?: string; folder_id?: string | null; is_cover?: boolean };
    }) => api.client.files.update({ params: { co, id: v.id }, body: v.body }),
    ...done,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.client.files.delete({ params: { co, id } }),
    ...done,
  });
  const restore = useMutation({
    mutationFn: (id: string) => api.client.files.restore({ params: { co, id } }),
    ...done,
  });
  const empty = useMutation({
    mutationFn: () => api.client.files.emptyTrash({ params: { co, id: task.id } }),
    ...done,
  });
  const newFolder = useMutation({
    mutationFn: (name: string) =>
      api.client.files.createFolder({
        params: { co, id: task.id },
        body: { name, ...(here ? { parent_id: here } : {}) },
      }),
    ...done,
  });
  const renameFolder = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      api.client.files.updateFolder({ params: { co, id: v.id }, body: { name: v.name } }),
    ...done,
  });
  const dropFolder = useMutation({
    mutationFn: (id: string) => api.client.files.deleteFolder({ params: { co, id } }),
    ...done,
  });

  // Parity P1 (design §3.1 Files "grid/list, sort … per-person prefs"): the view and the order.
  const [pref, setPref] = usePref(api, co, "task_files", {});
  const view = pref.view ?? "list";
  const sort = pref.sort ?? "date";
  const files = sortFiles(
    (live.data?.data ?? []).filter((f) => (f.folder_id ?? null) === here),
    sort,
  );
  // ←/→ in the preview step through the files in this order.
  const step = (d: number) => {
    if (!preview) return;
    const i = files.findIndex((f) => f.id === preview.id);
    const nextFile = files[(i + d + files.length) % files.length];
    if (nextFile) setPreview(nextFile);
  };
  const subfolders = allFolders.filter((f) => (f.parent_id ?? null) === here);
  const trashed = trash.data?.data ?? [];
  const crumbs: FolderItem[] = [];
  for (let cur = allFolders.find((f) => f.id === here); cur; ) {
    crumbs.unshift(cur);
    const parent = cur.parent_id;
    cur = parent ? allFolders.find((f) => f.id === parent) : undefined;
  }
  const total = live.data?.data.length ?? 0;

  return (
    <div ref={section}>
      <DrawerSection
        title={
          <span className="flex items-center gap-1.5">
            Files
            {total ? (
              <span className="text-sm font-normal text-muted-foreground tabular-nums">
                {total}
              </span>
            ) : null}
          </span>
        }
        data-slot="task-files"
        action={
          <div className="flex items-center gap-0.5">
            <NativeSelect
              size="sm"
              aria-label="Sort files"
              className="h-7 w-24"
              value={sort}
              onChange={(e) => setPref({ ...pref, sort: e.target.value as FileSort })}
            >
              <NativeSelectOption value="date">Newest</NativeSelectOption>
              <NativeSelectOption value="name">Name</NativeSelectOption>
              <NativeSelectOption value="size">Size</NativeSelectOption>
              <NativeSelectOption value="type">Type</NativeSelectOption>
            </NativeSelect>
            <IconAction
              label={view === "grid" ? "Show as a list" : "Show as a grid"}
              icon={view === "grid" ? ListIcon : LayoutGridIcon}
              pressed={view === "grid"}
              onClick={() => setPref({ ...pref, view: view === "grid" ? "list" : "grid" })}
            />
            <IconAction
              label="Attach files"
              icon={UploadIcon}
              onClick={() => input.current?.click()}
            />
            <IconAction
              label="New folder"
              icon={FolderPlusIcon}
              onClick={() => setRenaming({ kind: "new" })}
            />
            <IconAction
              label={showTrash ? "Back to files" : `Trash (${trashed.length})`}
              icon={showTrash ? ArchiveRestoreIcon : Trash2Icon}
              pressed={showTrash}
              onClick={() => setShowTrash((s) => !s)}
            />
          </div>
        }
        onDragOver={(e) => {
          if (![...e.dataTransfer.types].includes("Files")) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const dropped = [...e.dataTransfer.files];
          if (dropped.length) void upload(dropped);
        }}
        className={cn("relative rounded-lg", dragging && "outline-2 outline-dashed outline-ring")}
      >
        <input
          ref={input}
          type="file"
          multiple
          hidden
          aria-label="Choose files to attach"
          data-slot="file-input"
          onChange={(e) => {
            const chosen = [...(e.target.files ?? [])];
            e.target.value = "";
            if (chosen.length) void upload(chosen);
          }}
        />
        {dragging ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/80 text-sm font-medium">
            Drop to attach{crumbs.length ? ` to ${crumbs.at(-1)?.name}` : ""}
          </div>
        ) : null}
        {showTrash ? (
          <TrashList
            files={trashed}
            onRestore={(f) => restore.mutate(f.id)}
            onEmpty={() => setConfirm({ kind: "empty", count: trashed.length })}
          />
        ) : (
          <>
            {crumbs.length ? (
              <nav aria-label="Folder" className="mb-1 flex flex-wrap items-center gap-0.5 text-sm">
                <button
                  type="button"
                  className="rounded px-1 text-secondary hover:bg-muted"
                  onClick={() => setHere(null)}
                >
                  Files
                </button>
                {crumbs.map((c, i) => (
                  <span key={c.id} className="flex items-center gap-0.5">
                    <ChevronRightIcon aria-hidden="true" className="size-3.5 text-faint" />
                    <button
                      type="button"
                      aria-current={i === crumbs.length - 1 ? "location" : undefined}
                      className="rounded px-1 text-secondary hover:bg-muted aria-[current]:font-medium aria-[current]:text-foreground"
                      onClick={() => setHere(c.id)}
                    >
                      {c.name}
                    </button>
                  </span>
                ))}
              </nav>
            ) : null}
            <ul aria-label="Files" className="m-0 flex list-none flex-col p-0">
              {subfolders.map((f) => (
                <li
                  key={f.id}
                  data-slot="folder-row"
                  className="group/file relative flex min-h-10 items-center gap-2.5 rounded-md px-1.5 py-1 hover:bg-muted"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-sunken text-secondary">
                    <FolderIcon aria-hidden="true" className="size-4" />
                  </span>
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left text-sm font-medium hover:underline"
                    onClick={() => setHere(f.id)}
                  >
                    {f.name}
                    <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums">
                      {f.live_files} {f.live_files === 1 ? "file" : "files"}
                    </span>
                  </button>
                  <div className={cn("flex shrink-0 items-center gap-0.5", ON_HOVER)}>
                    <IconAction
                      label="Rename folder"
                      icon={PencilIcon}
                      onClick={() => setRenaming({ kind: "folder", folder: f })}
                    />
                    <IconAction
                      label="Delete folder"
                      icon={Trash2Icon}
                      className="hover:text-destructive"
                      onClick={() => setConfirm({ kind: "folder", folder: f })}
                    />
                  </div>
                </li>
              ))}
              {view === "grid" && files.length ? (
                <li className="mt-1">
                  <ul
                    aria-label="Files as a grid"
                    data-slot="files-grid"
                    className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(112px,1fr))] gap-2 p-0"
                  >
                    {files.map((f) => (
                      <li
                        key={f.id}
                        data-file={f.file_name}
                        data-cover={f.is_cover || undefined}
                        className="group/file relative"
                      >
                        <button
                          type="button"
                          onClick={() => setPreview(f)}
                          className={cn(
                            "flex w-full flex-col items-center gap-1.5 rounded-md border border-subtle p-2 text-left hover:bg-muted [&_[data-slot=file-thumb]]:size-16",
                            f.is_cover && "border-warning",
                          )}
                        >
                          <FileThumb api={api} co={co} file={f} />
                          <span className="w-full truncate text-xs">{f.file_name}</span>
                          <span className="text-2xs text-muted-foreground">
                            {formatBytes(f.bytes)}
                          </span>
                        </button>
                        {/* T4-VIEWS-MEDIA: pick the cover right on the tile (a filled star). */}
                        {f.is_cover ? (
                          <StarIcon
                            aria-label="Cover"
                            className="pointer-events-none absolute top-1.5 left-1.5 size-3.5 fill-current text-warning"
                          />
                        ) : null}
                        <div
                          className={cn(
                            "absolute top-1 right-1 flex items-center rounded-md bg-card/90",
                            ON_HOVER,
                          )}
                        >
                          {f.preview === "image" ? (
                            <IconAction
                              label={f.is_cover ? "Remove cover" : "Make cover"}
                              icon={StarIcon}
                              pressed={f.is_cover}
                              onClick={() =>
                                update.mutate({ id: f.id, body: { is_cover: !f.is_cover } })
                              }
                            />
                          ) : null}
                          <IconAction
                            label="Rename"
                            icon={PencilIcon}
                            onClick={() => setRenaming({ kind: "file", file: f })}
                          />
                          <IconAction
                            label="Delete"
                            icon={Trash2Icon}
                            className="hover:text-destructive"
                            onClick={() => setConfirm({ kind: "file", file: f })}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : null}
              {(view === "grid" ? [] : files).map((f) => (
                <FileRow
                  key={f.id}
                  api={api}
                  co={co}
                  file={f}
                  actions={{
                    onPreview: setPreview,
                    onRename: (x) => setRenaming({ kind: "file", file: x }),
                    onDelete: (x) => setConfirm({ kind: "file", file: x }),
                    onCover: (x) => update.mutate({ id: x.id, body: { is_cover: !x.is_cover } }),
                  }}
                />
              ))}
              {uploading.map((u) => (
                <li
                  key={u.id}
                  className="flex min-h-12 items-center gap-2.5 px-1.5 text-sm text-muted-foreground"
                  data-slot="file-uploading"
                >
                  <span className="flex size-9 items-center justify-center">
                    <Spinner />
                  </span>
                  Uploading {u.name}…
                </li>
              ))}
            </ul>
            {!files.length && !subfolders.length && !uploading.length && !live.isPending ? (
              <button
                type="button"
                data-slot="files-empty"
                onClick={() => input.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-strong px-3 py-4 text-sm text-muted-foreground hover:bg-muted"
              >
                <PaperclipIcon aria-hidden="true" className="size-4" />
                {here ? "This folder is empty. " : ""}Drop files here, paste, or click to attach
              </button>
            ) : null}
          </>
        )}
      </DrawerSection>

      <PreviewDialog api={api} co={co} file={preview} onClose={() => setPreview(null)} />
      {preview ? <ArrowKeys onStep={step} /> : null}

      {renaming ? (
        <RenameDialog
          key={
            renaming.kind === "file"
              ? renaming.file.id
              : renaming.kind === "folder"
                ? renaming.folder.id
                : "new"
          }
          title={
            renaming.kind === "file"
              ? "Rename file"
              : renaming.kind === "folder"
                ? "Rename folder"
                : "New folder"
          }
          initial={
            renaming.kind === "file"
              ? renaming.file.file_name
              : renaming.kind === "folder"
                ? renaming.folder.name
                : ""
          }
          {...(renaming.kind === "file"
            ? { hint: "A “/” moves it into that folder (made if needed), e.g. “Final/brief.pdf”." }
            : {})}
          onClose={() => setRenaming(null)}
          onSave={(name) => {
            if (renaming.kind === "file")
              update.mutate({ id: renaming.file.id, body: { file_name: name } });
            else if (renaming.kind === "folder")
              renameFolder.mutate({ id: renaming.folder.id, name });
            else newFolder.mutate(name);
            setRenaming(null);
          }}
        />
      ) : null}

      <AlertDialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-slot="file-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "file"
                ? `Delete “${confirm.file.file_name}”?`
                : confirm?.kind === "folder"
                  ? `Delete the folder “${confirm.folder.name}”?`
                  : "Empty the trash?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "file"
                ? "It moves to the task's trash, where it can be restored until it is purged."
                : confirm?.kind === "folder"
                  ? `Its ${confirm.folder.live_files} ${confirm.folder.live_files === 1 ? "file moves" : "files move"} to the trash (restorable); the folder and its subfolders are removed.`
                  : `${confirm?.kind === "empty" ? confirm.count : 0} trashed ${confirm?.kind === "empty" && confirm.count === 1 ? "file is" : "files are"} deleted for good. This can't be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirm?.kind === "file") remove.mutate(confirm.file.id);
                else if (confirm?.kind === "folder") dropFolder.mutate(confirm.folder.id);
                else if (confirm?.kind === "empty") empty.mutate();
                setConfirm(null);
              }}
            >
              {confirm?.kind === "empty" ? "Empty trash" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** ←/→ while a file's preview is open: the previous or next file (Parity P1, design §3.1). */
function ArrowKeys({ onStep }: { onStep: (d: number) => void }) {
  const latest = useRef(onStep);
  latest.current = onStep;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") latest.current(-1);
      else if (e.key === "ArrowRight") latest.current(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return null;
}

function TrashList({
  files,
  onRestore,
  onEmpty,
}: {
  files: FileItem[];
  onRestore: (f: FileItem) => void;
  onEmpty: () => void;
}) {
  if (!files.length)
    return (
      <p className="py-2 text-sm text-muted-foreground" data-slot="trash-empty">
        The trash is empty.
      </p>
    );
  return (
    <div data-slot="file-trash">
      <ul aria-label="Trash" className="m-0 flex list-none flex-col p-0">
        {files.map((f) => {
          const Icon = iconOf(f);
          const left = daysLeft(f.purge_at);
          return (
            <li
              key={f.id}
              data-file={f.file_name}
              className="flex min-h-11 items-center gap-2.5 rounded-md px-1.5 py-1 hover:bg-muted"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-sunken text-faint">
                <Icon aria-hidden="true" className="size-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-secondary line-through decoration-faint">
                  {f.file_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatBytes(f.bytes)} · purged in {left} {left === 1 ? "day" : "days"}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRestore(f)}
                aria-label={`Restore ${f.file_name}`}
              >
                <ArchiveRestoreIcon aria-hidden="true" />
                Restore
              </Button>
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex justify-end">
        <Button variant="ghost" size="sm" className="text-destructive" onClick={onEmpty}>
          <Trash2Icon aria-hidden="true" />
          Empty trash
        </Button>
      </div>
    </div>
  );
}
